import * as XLSX from "xlsx";

// Colunas esperadas pelo serviço de importação (mesmas do modelo CSV original).
export const COLUNAS_IMPORTACAO = ["ativo", "bloco", "numero", "fracao", "nome", "documento", "email", "celular"];

const normalizar = texto =>
  String(texto === undefined || texto === null ? "" : texto)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const celulaParaTexto = valor => {
  if (valor === undefined || valor === null) return "";
  if (valor instanceof Date) return valor.toISOString().substr(0, 10);
  return String(valor).trim().replace(/;/g, ",").replace(/[\r\n]+/g, " ");
};

const lerArquivo = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
    reader.onload = e => resolve(e.target.result);
    reader.readAsArrayBuffer(file);
  });

// Converte XLS/XLSX/CSV para o CSV (separado por ";") que o serviço de importação espera.
// Resolve com { file, linhas } ou rejeita com Error de mensagem amigável.
export const prepararArquivoImportacao = async file => {
  const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  if (![".xls", ".xlsx", ".csv"].includes(ext)) {
    throw new Error("Selecione um arquivo XLS, XLSX ou CSV");
  }

  let matriz;
  try {
    const buffer = await lerArquivo(file);
    const workbook = ext === ".csv"
      ? XLSX.read(new TextDecoder("utf-8").decode(buffer), { type: "string", FS: ";", raw: true })
      : XLSX.read(buffer, { type: "array", cellDates: true, raw: true });
    const planilha = workbook.Sheets[workbook.SheetNames[0]];
    matriz = XLSX.utils.sheet_to_json(planilha, { header: 1, raw: true, defval: "", blankrows: false });
  } catch (e) {
    throw new Error("Não foi possível ler o arquivo. Verifique se ele não está corrompido.");
  }

  if (!matriz.length) {
    throw new Error("O arquivo está vazio");
  }

  const cabecalho = matriz[0].map(normalizar);
  const faltando = COLUNAS_IMPORTACAO.filter(c => cabecalho.indexOf(c) === -1);
  if (faltando.length) {
    throw new Error(`Colunas ausentes no cabeçalho: ${faltando.join(", ")}. Use o modelo disponível nesta página.`);
  }

  const indices = COLUNAS_IMPORTACAO.map(c => cabecalho.indexOf(c));
  const linhas = matriz
    .slice(1)
    .filter(linha => linha.some(c => celulaParaTexto(c) !== ""))
    .map(linha =>
      indices.map((indice, i) => {
        const texto = celulaParaTexto(linha[indice]);
        // fração aceita vírgula decimal (padrão do Excel em pt-BR)
        return COLUNAS_IMPORTACAO[i] === "fracao" ? texto.replace(",", ".") : texto;
      })
    );

  if (!linhas.length) {
    throw new Error("Nenhuma linha de dados encontrada no arquivo");
  }

  const csv = [COLUNAS_IMPORTACAO, ...linhas].map(l => l.join(";")).join("\n") + "\n";
  const nome = file.name.replace(/\.[^.]+$/, "") + ".csv";
  return { file: new File([csv], nome, { type: "text/csv" }), linhas: linhas.length, dados: linhas, original: file.name };
};

// Conferência prévia (apenas avisos: quem decide é o serviço de importação).
// Devolve [{ linha, problemas: [] }] com a linha contada como na planilha (cabeçalho = 1).
export const conferirLinhas = linhas => {
  const col = c => COLUNAS_IMPORTACAO.indexOf(c);
  const avisos = [];
  const vistos = {};

  linhas.forEach((l, i) => {
    const problemas = [];
    const ativo = normalizar(l[col("ativo")]);
    const numero = l[col("numero")];
    const nome = l[col("nome")];
    const documento = l[col("documento")].replace(/\D/g, "");
    const email = l[col("email")];

    if (!["s", "n", "sim", "nao"].includes(ativo)) problemas.push('"ativo" deve ser s ou n');
    if (!numero) problemas.push("número da unidade vazio");
    if (!nome) problemas.push("nome vazio");
    if (documento && documento.length !== 11 && documento.length !== 14) problemas.push("documento deve ter 11 (CPF) ou 14 (CNPJ) dígitos");
    if (!documento) problemas.push("documento vazio");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) problemas.push("e-mail inválido");

    const chave = `${normalizar(l[col("bloco")])}|${normalizar(numero)}`;
    if (numero) {
      if (vistos[chave] !== undefined) problemas.push(`unidade repetida (igual à linha ${vistos[chave]})`);
      else vistos[chave] = i + 2;
    }

    if (problemas.length) avisos.push({ linha: i + 2, problemas });
  });

  return avisos;
};
